<script setup>
  import { ref, computed, watchEffect } from 'vue';
  import AccordianBlock from '@/components/AccordianBlock.vue';
  import ProForecastSpotTitleAction from '@/components/ProForecastSpotTitleAction.vue';
  import Icon from '@/components/icons/Icon.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import ControlButton from "@/components/controls/ControlButton.vue";
  import CloseButton from '@/components/CloseButton.vue';

  const { $WEB_VIEW, $FEATURE_VERSION = 0 } = useSettingsGlobals();

  const emit = defineEmits(['accordian:toggle']);
  const props = defineProps({
    spotForecast: {
      type: Object,
      required: true
    },
    spotForecastState: {
      type: Object,
      required: false
    },
    spotForecastSettings: {
      type: Object,
      default: () => {}
    }
  });

  const featureVersionMinimum = 1.0;
  const showActionButton = !$WEB_VIEW || $WEB_VIEW && $FEATURE_VERSION >= featureVersionMinimum;

  const collapsed = ref(props.spotForecastState?.collapsed);
  const showHint = ref(false);

  const spotForecast = computed(() => {
      // Rebuilds the forecast data returned from the api in order to convert the multi-hour blocks into hourly blocks
      return {
        ...props.spotForecast,
        forecast: getHourBlocks(props.spotForecast.forecast)
      };
  });

  const showSpecialNote = computed(() => !collapsed.value && spotForecast.value.spot_note && spotForecast.value.spot_note.length > 0);
  const previewColors = computed(() => spotForecast.value.forecast.map(forecastBlock => `${forecastBlock.color}`).join(', '));
  
  const showDirectionArrow = computed(() => {
    return Object.hasOwnProperty.call(props.spotForecastSettings, 'showDirectionArrow') 
      ? props.spotForecastSettings.showDirectionArrow 
      : false
  });

  const showDirectionValue = computed(() => {
    return Object.hasOwnProperty.call(props.spotForecastSettings, 'showDirectionValue') 
      ? props.spotForecastSettings.showDirectionValue 
      : false
  });

  const directionValueType = computed(() => {
    return Object.hasOwnProperty.call(props.spotForecastSettings, 'directionValueType') 
      ? props.spotForecastSettings.directionValueType 
      : ''
  });

  const directionLabelText = computed(() => {
    return showDirectionValue.value && directionValueType.value === 'degrees'
      ? 'Dir/Deg'
      : 'Dir'
  })

  const getHourBlocks = (forecast) => {
    const hourBlocks = [];

    forecast.forEach(timeBlock => {
      timeBlock.intervals.forEach((block, i) => {

        const forecastHour = timeBlock.time_stamp_hour + i;

        if (forecastHour === 6 || forecastHour === 7) return;

        const hourBlock =  {
          id: `${forecast.spot_id}-${block}`,
          hour: getSpotForecastHour(forecastHour),
          direction: timeBlock.direction,
          degrees: getDegrees(timeBlock.direction),
          min_speed: timeBlock.min_speed,
          max_speed: timeBlock.max_speed,
          color: `#${timeBlock.wind_color}`
        }

        hourBlocks.push(hourBlock);
      })
    });

    return hourBlocks;
  }

  const getSpotForecastHour = (hour) => {
    const dayPeriod = hour >= 12 ? 'P' : 'A';
    const dayPeriodHTML = `<span class="jwx-pro-forecast__spot-forecast-day-period">${dayPeriod}</span>`;

    return hour % 12 === 0 
      ? `12${dayPeriodHTML}` 
      : `${hour % 12}${dayPeriodHTML}`;
  }

  const getDegrees = (direction) => {
    return direction === 'N' ? 0
      : direction === 'NNE' ? 22.5
      : direction === 'NE' ? 45
      : direction === 'ENE' ? 67.5
      : direction === 'E' ? 90
      : direction === 'ESE' ? 112.5
      : direction === 'SE' ? 135
      : direction === 'SSE' ? 157.5
      : direction === 'S' ? 180
      : direction === 'SSW' ? 202.5
      : direction === 'SW' ? 225
      : direction === 'WSW' ? 247.5
      : direction === 'W' ? 270
      : direction === 'WNW' ? 292.5
      : direction === 'NW' ? 315
      : direction === 'NNW' ? 337.5
      : 0;
  }

  const handleAccordianToggle = () => {
    collapsed.value = !collapsed.value;
    
    emit('accordian:toggle', { collapsed: collapsed.value, spotId: props.spotForecast.spot_id });
  }

  watchEffect(() => {
    collapsed.value = props.spotForecastState && props.spotForecastState.collapsed;
  });

</script>

<template>
  <AccordianBlock
    @accordian:toggle="handleAccordianToggle"
    :key="props.spotForecast.spot_id"
    :id="props.spotForecast.spot_id"
    :collapsed="collapsed"
    class="jwx-pro-forecast__spot-forecast-block"
    :data-has-special-note="`${showSpecialNote === true}`"
  >
    <template #title-content>
      <h3 v-if="showActionButton" class="jwx-block__title">
        <ProForecastSpotTitleAction
          :spotId="props.spotForecast.spot_id"
          :webView="$WEB_VIEW"
        >
          {{ props.spotForecast.spot_name }}
          <Icon name="share" class="jwx-block__title-action-icon" />
          <span class="sr-only">Click to open complete report.</span>
        </ProForecastSpotTitleAction>
      </h3>
      <h3 v-else class="jwx-block__title">
        {{ props.spotForecast.spot_name }}
      </h3>
    </template>
    <template #header-content>
      <div
        v-if="collapsed"
        class="jwx-pro-forecast__preview-bar"
        :style="{ background: `linear-gradient(to right, ${previewColors})` }"
      ></div>
    </template>
    <template #body>
      <p v-if="showSpecialNote" class="jwx-pro-forecast__spot-forecast-text" v-html="props.spotForecast.spot_note"></p>
      <div class="jwx-pro-forecast__forecast-table">
        <div class="jwx-pro-forecast__forecast-table-info-action">
          <ControlButton
            @click.prevent="showHint = !showHint"
            data-surface="1"
            data-shape="circle"
            data-size="small"
          >
            <Icon name="question-mark" class="jwx-button-control__icon" />
          </ControlButton>
        </div>
        <Transition name="fade">
          <div
            v-show="showHint"
            class="jwx-pro-forecast__forecast-table-info-hint-container"
          >
            <div
              class="jwx-user-hint jwx-pro-forecast__forecast-table-info-hint"
            >
              <p class="jwx-user-hint__text">
                nchgvyfrvcffcbmjjnbhv vb. nbnmk,l,mknjbhgtfrderty7u8ytre
              </p>
              <CloseButton v-if="!props.hideCloseButton" @click.prevent="showHint = false"/>

            </div>
          </div>
        </Transition>
        <div class="jwx-pro-forecast__forecast-table-header">
          <p class="jwx-pro-forecast__forecast-table-header-row">&nbsp;</p>
          <div class="jwx-pro-forecast__spot-forecast-header-labels">
            <p
              class="jwx-pro-forecast__forecast-table-header-row"
              data-content="wind-range"
            >Wind<br>Range</p>
            <p
              class="jwx-pro-forecast__forecast-table-header-row"
              :data-direction-text="showDirectionValue"
              data-content="direction"
              v-html="directionLabelText"
            ></p>
          </div>
        </div>
        <div class="jwx-pro-forecast__forecast-table-body">
          <div
            v-for="forecastBlock in spotForecast.forecast"
            :key="forecastBlock.spot_id"
            class="jwx-pro-forecast__forecast-table-column"
          >
            <p class="jwx-pro-forecast__spot-forecast-hour" v-html="forecastBlock.hour"></p>
            <div
              class="jwx-pro-forecast__spot-forecast-hourly-data"
              :data-background-dark="forecastBlock.max_speed >= 20"
              :style="{ backgroundColor: forecastBlock.color }"
            >
              <div class="jwx-pro-forecast__spot-forecast-windspeed">
                <p class="jwx-pro-forecast__spot-forecast-max-windspeed">{{ forecastBlock.max_speed }}</p>
                <p class="jwx-pro-forecast__spot-forecast-min-windspeed">{{ forecastBlock.min_speed }}</p>
              </div>
              <div class="jwx-pro-forecast__spot-forecast-direction" 
                :data-direction-arrow="showDirectionArrow" 
                :data-direction-value="showDirectionValue" 
                data-content="direction"
              >
                <Icon v-if="showDirectionArrow" name="wind-direction-filled" :style="{ transform: `translateY(-1px) rotate(${forecastBlock.degrees}deg)` }" />
                <p v-if="showDirectionValue && directionValueType === 'degrees'" class="jwx-pro-forecast__spot-forecast-direction-text">{{ Math.ceil(forecastBlock.degrees) }}</p>
                <p v-if="showDirectionValue && directionValueType === 'cardinal'" class="jwx-pro-forecast__spot-forecast-direction-text">{{ forecastBlock.direction }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AccordianBlock>
</template>